import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, MapPin, Phone, Mail, ArrowLeft, CheckCircle, Car, ChevronDown, ChevronUp, Image, Settings, Fuel, Users, Shield, Star, Check, AlertCircle, Search, X } from 'lucide-react';

import sedan1 from '../Assets/sedan.png';
import SUV from '../Assets/Ertiga.png';
import Innova from '../Assets/Innova (1).png';
import Urbania from '../Assets/67851_1707226826-removebg-preview.png';
import Tempo from '../Assets/tempo5-removebg-preview.png';
import Luxury from '../Assets/car insurance marketing promotion (10).png';
import etiosImage from '../Assets/ETIOS1-removebg-preview.png';
import innovaHybridImage from '../Assets/Hycross (1).png';
import swiftImage from '../Assets/swift4-removebg-preview.png';
import kiaCarensImage from '../Assets/carrens.png'; 
import toyotaRumionImage from '../Assets/2023-Toyota-Rumion-1.png'
import fortunerImage from '../Assets/Innova (1).png'

// Default placeholder image
const defaultVehicleImage = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop';
const vehicleCategories = [
  {
    id: 'innova-hybrid',
    name: 'Prime Suv - Toyota Hycross / Hybrid',
    images: [innovaHybridImage],
    description: 'Hybrid MPV with superior fuel efficiency',
    features: ['6+1 & 7+1 seater', 'AC Available', 'Premium Features'],
    specifications: {
      seats: 7,
      luggage: 5,
      ac: true,
      type: 'Hybrid MPV',
      engine: '2.0L Hybrid',
      transmission: 'Automatic',
      fuel: 'Petrol Hybrid'
    },
    pricing: {
      perKm: '₹22/km',
      outstation: '₹25/km'
    },
    highlights: [
      'Eco-friendly travel with premium comfort',
      'Corporate executive transfers',
      'Family trips with enhanced fuel efficiency'
    ],
    amenities: ['Wireless Charging', 'Premium Sound System', 'Ambient Lighting'],
    safety: ['7 Airbags', 'Vehicle Stability Control', '360 Camera']
  },
  {
    id: 'innova',
    name: 'SUV – Toyota Innova Crysta',
    images: [Innova],
    description: 'Premium MPV for family trips',
    features: ['6+1 & 7+1 seater', 'Luxury Interior', 'Long Distance'],
    specifications: {
      seats: 7,
      luggage: 5,
      ac: true,
      type: 'Luxury MPV',
      engine: '2.4L Diesel',
      transmission: 'Automatic',
      fuel: 'Diesel'
    },
    pricing: {
      perKm: '₹18/km',
      outstation: '₹27/km'
    },
    highlights: [
      'Corporate travel & executive transfers',
      'Family vacations & pilgrimages',
      'Long distance business trips'
    ],
    amenities: ['Captain Seats', 'Wooden Finish', 'Ambient Lighting'],
    safety: ['7 Airbags', 'Vehicle Stability Control', 'Parking Sensors']
  },
  {
    id: 'suv',
    name: 'Maruti Ertiga',
    images: [SUV],
    description: 'Spacious and powerful for all terrains',
    features: ['6+1 seater', 'Premium Comfort', 'All Terrain'],
    specifications: {
      seats: 7,
      luggage: 4,
      ac: true,
      type: 'Premium SUV',
      engine: '1.5L Petrol',
      transmission: 'Automatic',
      fuel: 'Petrol'
    },
    pricing: {
      perKm: '₹14/km',
      outstation: '₹18/km'
    },
    highlights: [
      'Family trips & weekend getaways',
      'Outstation travel with comfort',
      'Group outings & corporate events'
    ],
    amenities: ['Premium Sound System', 'Rear Entertainment', 'Charging Ports'],
    safety: ['6 Airbags', 'ESP', 'Hill Hold Assist']
  },
  {
    id: 'toyota-rumion',
    name: 'Toyota Rumion',
    images: [toyotaRumionImage],
    description: 'Spacious and reliable MPV for comfortable family travel',
    features: ['7+1 seater', 'Spacious Cabin', 'Fuel Efficient'],
    specifications: {
      seats: 8,
      luggage: 4,
      ac: true,
      type: 'Family MPV',
      engine: '1.5L Petrol',
      transmission: 'Automatic',
      fuel: 'Petrol'
    },
    pricing: {
      perKm: '₹14/km',
      outstation: '₹18/km'
    },
    highlights: [
      'Perfect for family vacations',
      'Spacious and comfortable interior',
      'Reliable performance for long journeys'
    ],
    amenities: ['Spacious Cabin', 'Comfortable Seating', 'AC Vents'],
    safety: ['6 Airbags', 'ABS', 'Rear Parking Sensors']
  },
  {
    id: 'kia-carens',
    name: 'Kia Carens',
    images: [kiaCarensImage],
    description: 'Premium SUV with modern features and spacious interior',
    features: ['6+1 & 7+1 seater', 'Modern Features', 'Spacious Interior'],
    specifications: {
      seats: 7,
      luggage: 4,
      ac: true,
      type: 'Premium SUV',
      engine: '1.5L Petrol',
      transmission: 'Automatic',
      fuel: 'Petrol'
    },
    pricing: {
      perKm: '₹14/km',
      outstation: '₹19/km'
    },
    highlights: [
      'Modern family SUV with premium features',
      'Spacious and comfortable interior',
      'Perfect for city and highway travel'
    ],
    amenities: ['Touchscreen Infotainment', 'Climate Control', 'Rear AC Vents'],
    safety: ['6 Airbags', 'ABS with EBD', 'Rear Parking Camera']
  },
  {
    id: 'sedan',
    name: 'Prime Sedan - Maruti Ciaz',
    images: [sedan1],
    description: 'Comfortable and efficient for city rides',
    features: ['4+1 seater', 'AC Available', 'Fuel Efficient'],
    specifications: {
      seats: 5,
      luggage: 2,
      ac: true,
      type: 'Premium Sedan',
      engine: '1.5L Petrol',
      transmission: 'Manual/Automatic',
      fuel: 'Petrol'
    },
    pricing: {
      perKm: '₹16/km',
      outstation: '₹15/km'
    },
    highlights: [
      'Perfect for airport transfers',
      'City commute & business meetings',
      'Comfortable long-distance travel'
    ],
    amenities: ['Free Water Bottles', 'Newspaper', 'Phone Charger'],
    safety: ['Airbags', 'ABS', 'First Aid Kit']
  },
  {
    id: 'swift',
    name: 'Swift Dzire',
    images: [swiftImage],
    description: 'Compact sedan perfect for city commutes',
    features: ['4+1 seater', 'Compact Size', 'Easy Parking'],
    specifications: {
      seats: 5,
      luggage: 2,
      ac: true,
      type: 'Compact Sedan',
      engine: '1.2L Petrol',
      transmission: 'Manual/Automatic',
      fuel: 'Petrol'
    },
    pricing: {
      perKm: '₹11/km',
      outstation: '₹13/km'
    },
    highlights: [
      'Perfect for narrow city streets',
      'Easy parking in tight spaces',
      'Quick city navigation'
    ],
    amenities: ['Free Water Bottles', 'Phone Charger', 'Music System'],
    safety: ['Airbags', 'ABS', 'Rear Parking Sensors']
  },
  {
    id: 'etios',
    name: 'Prime Sedan-Etios',
    images: [etiosImage],
    description: 'Reliable and fuel-efficient sedan',
    features: ['4+1 seater', 'Great Mileage', 'Economical'],
    specifications: {
      seats: 5,
      luggage: 2,
      ac: true,
      type: 'Economical Sedan',
      engine: '1.2L Petrol',
      transmission: 'Manual',
      fuel: 'Petrol'
    },
    pricing: {
      perKm: '₹12/km',
      outstation: '₹16/km'
    },
    highlights: [
      'Perfect for budget-conscious travelers',
      'City commute & daily travel',
      'Fuel-efficient long-distance travel'
    ],
    amenities: ['Free Water Bottles', 'Phone Charger', 'Basic Entertainment'],
    safety: ['Airbags', 'ABS', 'Safety Belts']
  },
  {
    id: 'fortuner',
    name: 'Toyota Fortuner',
    images: [fortunerImage],
    description: 'Premium SUV with powerful performance and luxury features',
    features: ['7+1 seater', '4x4 Available', 'Luxury Interior'],
    specifications: {
      seats: 8,
      luggage: 6,
      ac: true,
      type: 'Premium SUV',
      engine: '2.8L Diesel',
      transmission: 'Automatic',
      fuel: 'Diesel'
    },
    pricing: {
      perKm: '₹65/km',
      outstation: '₹65/km'
    },
    highlights: [
      'Powerful performance for all terrains',
      'Luxury interior with premium features',
      'Perfect for adventurous journeys'
    ],
    amenities: ['Leather Seats', 'Sunroof', 'Premium Audio System'],
    safety: ['7 Airbags', 'Vehicle Stability Control', 'Hill Descent Control']
  },
  {
    id: 'hcross',
    name: 'Luxury Urbania',
    images: [Urbania],
    description: 'Ultra-luxury van for groups',
    features: ['10, 12, 13, 14 and 17 seater', 'Ultra Luxury', 'Group Travel'],
    specifications: {
      seats: 17,
      luggage: 8,
      ac: true,
      type: 'Executive MPV',
      engine: '2.0L Diesel',
      transmission: 'Manual',
      fuel: 'Diesel'
    },
    pricing: {
      perKm: '₹45/km',
      outstation: '₹50/km'
    },
    highlights: [
      'Executive board meetings',
      'VIP transfers & diplomatic travel',
      'Luxury wedding arrangements'
    ],
    amenities: ['WiFi Hotspot', 'Mini Bar', 'Massage Seats'],
    safety: ['Multiple Airbags', '360 Camera', 'Blind Spot Monitor']
  },
  {
    id: 'tempo',
    name: 'Tempo Traveller',
    images: [Tempo],
    description: 'Perfect for large groups',
    features: ['12+1 seater - A/C & Non A/C', 'Group Friendly', 'Tourist Special'],
    specifications: {
      seats: 13,
      luggage: 6,
      ac: true,
      type: 'People Carrier',
      engine: '2.6L Diesel',
      transmission: 'Manual',
      fuel: 'Diesel'
    },
    pricing: {
      perKm: '₹30/km',
      outstation: '₹35/km'
    },
    highlights: [
      'Wedding parties & ceremonies',
      'Corporate events & conferences',
      'Pilgrimage tours & group travel'
    ],
    amenities: ['PA System', 'Multiple Charging Ports', 'Storage Compartments'],
    safety: ['Safety Belts', 'Fire Extinguisher', 'Emergency Exits']
  },
  {
    id: 'luxury',
    name: 'Premium Buses',
    images: [Luxury],
    description: 'Premium cars for special occasions',
    features: ['21, 30, 50 Seater- A/C & Non A/C', 'VIP Service', 'Special Events'],
    specifications: {
      seats: 50,
      luggage: 10,
      ac: true,
      type: 'Luxury Coach',
      engine: '5.0L Diesel',
      transmission: 'Manual',
      fuel: 'Diesel'
    },
    pricing: {
      perKm: '₹60/km',
      outstation: '₹65/km'
    },
    highlights: [
      'Weddings & special celebrations',
      'VIP corporate transfers',
      'Anniversary & birthday surprises'
    ],
    amenities: ['Luxury Seating', 'Entertainment System', 'Onboard Facilities'],
    safety: ['Advanced Driver Assistance', 'GPS Tracking', 'Premium Security']
  }
];

// Safe data getter functions
const getVehicleImage = (vehicle) => {
  if (!vehicle) return defaultVehicleImage;
  if (vehicle.images && vehicle.images.length > 0 && vehicle.images[0]) {
    return vehicle.images[0];
  }
  return defaultVehicleImage;
};

const getVehicleName = (vehicle) => {
  return vehicle?.name || 'Premium Vehicle';
};

const getVehicleDescription = (vehicle) => {
  return vehicle?.description || 'Comfortable and reliable transportation';
};

const getVehiclePricing = (vehicle) => {
  return vehicle?.pricing || {
    perKm: '₹15/km',
    perHour: '₹500/hour',
    minCharge: '₹250',
    airportDrop: '₹2000',
    outstation: '₹18/km'
  };
};

const getVehicleFeatures = (vehicle) => {
  return vehicle?.features || ['AC Available', 'Comfortable Seats', 'Professional Driver'];
};

const getVehicleSpecifications = (vehicle) => {
  return vehicle?.specifications || {
    seats: 4,
    luggage: 2,
    ac: true,
    type: 'Standard',
    engine: 'Not specified',
    transmission: 'Not specified',
    fuel: 'Not specified'
  };
};

// Location Autocomplete Component - FIXED VERSION
const LocationAutocomplete = ({
  value,
  onChange,
  placeholder,
  type = 'pickup',
  onLocationSelect
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Sync with parent value
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm && searchTerm.length > 2) {
        fetchLocations(searchTerm);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchLocations = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      // Using OpenStreetMap Nominatim API (free, no API key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=8&addressdetails=1`
      );

      if (response.ok) {
        const data = await response.json();
        const formattedSuggestions = data.map(item => ({
          display_name: item.display_name,
          lat: item.lat,
          lon: item.lon,
          type: item.type,
          importance: item.importance
        }));

        setSuggestions(formattedSuggestions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // Create a proper event object for the parent
    const syntheticEvent = {
      target: {
        name: type === 'pickup' ? 'pickupAddress' : 'dropAddress',
        value: newValue
      }
    };
    onChange(syntheticEvent);
    
    // Show suggestions only if there's a search term
    if (newValue.length > 2) {
      // Suggestions will be shown when fetchLocations completes
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const displayName = suggestion.display_name;
    setSearchTerm(displayName);
    
    // Create proper event for parent
    const syntheticEvent = {
      target: {
        name: type === 'pickup' ? 'pickupAddress' : 'dropAddress',
        value: displayName
      }
    };
    onChange(syntheticEvent);

    if (onLocationSelect) {
      onLocationSelect({
        address: displayName,
        lat: suggestion.lat,
        lon: suggestion.lon
      });
    }

    // Close suggestions immediately after selection
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const clearInput = () => {
    setSearchTerm('');
    const syntheticEvent = {
      target: {
        name: type === 'pickup' ? 'pickupAddress' : 'dropAddress',
        value: ''
      }
    };
    onChange(syntheticEvent);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    // Only show suggestions if we have search term and suggestions
    if (searchTerm && searchTerm.length > 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = (e) => {
    // Use a small delay to allow for clicking on suggestions
    setTimeout(() => {
      // Check if the related target (the element that will receive focus) is inside our container
      const relatedTarget = e.relatedTarget;
      if (containerRef.current && !containerRef.current.contains(relatedTarget)) {
        setShowSuggestions(false);
      }
    }, 200);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-10"
          placeholder={placeholder}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {searchTerm && (
            <button
              type="button"
              onClick={clearInput}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <Search className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Searching locations...
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.lat}-${suggestion.lon}-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur on mousedown
                className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
              >
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.display_name.split(',')[0]}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {suggestion.display_name.split(',').slice(1).join(',').trim()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}

          {suggestions.length === 0 && !isLoading && searchTerm.length > 2 && (
            <div className="p-3 text-center text-gray-500 text-sm">
              No locations found. Try a different search term.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BookingForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [vehicleSelectionError, setVehicleSelectionError] = useState('');
  const [isVehicleSelected, setIsVehicleSelected] = useState(false);

  // Add refs to focus on the form
  const formRef = useRef(null);
  const vehicleSectionRef = useRef(null);

  // Get vehicle data and trip data from navigation state
  const initialVehicle = location.state?.vehicle || null;
  const tripData = location.state?.tripData;
  const preFilledData = location.state?.preFilledData;

  // Initialize form data
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    pickupAddress: preFilledData?.pickupAddress || '',
    dropAddress: preFilledData?.dropAddress || '',
    pickupDate: '',
    pickupTime: '',
    tripType: preFilledData?.tripType || 'oneWay',
    vehicleType: '',
    vehicleName: '',
    specialRequirements: tripData ? `Booking for ${tripData.from} to ${tripData.to} - ${tripData.distance} journey` : '',
    passengers: 1,
    luggage: 1,
    paymentMethod: 'cash'
  });

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [locationData, setLocationData] = useState({
    pickup: null,
    drop: null
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when booking is confirmed
  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [isSubmitted]);

  // Focus on form when component mounts
  useEffect(() => {
    // Scroll to top of the page immediately
    window.scrollTo(0, 0);

    // Focus on the form container with a slight delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Focus on vehicle section when no vehicle is selected
  useEffect(() => {
    if (!selectedVehicle && vehicleSectionRef.current) {
      const timer = setTimeout(() => {
        vehicleSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [selectedVehicle]);

  // Update selected vehicle when formData.vehicleType changes
  useEffect(() => {
    if (formData.vehicleType) {
      const vehicle = vehicleCategories.find(v => v.id === formData.vehicleType);
      if (vehicle) {
        setSelectedVehicle(vehicle);
        setIsVehicleSelected(true);
        setVehicleSelectionError('');
      }
    }
  }, [formData.vehicleType]);

  // Update form data when preFilledData is available
  useEffect(() => {
    if (preFilledData) {
      setFormData(prev => ({
        ...prev,
        pickupAddress: preFilledData.pickupAddress || prev.pickupAddress,
        dropAddress: preFilledData.dropAddress || prev.dropAddress,
        tripType: preFilledData.tripType || prev.tripType
      }));
    }
  }, [preFilledData]);

  // Set initial vehicle if provided from navigation
  useEffect(() => {
    if (initialVehicle) {
      handleVehicleSelect(initialVehicle);
    }
  }, [initialVehicle]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVehicleSelect = (vehicle) => {
    if (!vehicle) return;

    setFormData(prev => ({
      ...prev,
      vehicleType: vehicle.id,
      vehicleName: getVehicleName(vehicle)
    }));
    setSelectedVehicle(vehicle);
    setIsVehicleSelected(true);
    setShowVehicleSelector(false);
    setVehicleSelectionError('');
  };

  const handleLocationSelect = (location, type) => {
    setLocationData(prev => ({
      ...prev,
      [type]: location
    }));
  };

  const generateBookingNumber = () => {
  // Get current counter from localStorage or start from 1
  let counter = parseInt(localStorage.getItem('bookingCounter')) || 1;
  
  // Generate booking number
  const bookingNumber = `${counter}`;
  
  // Increment and save counter for next booking
  localStorage.setItem('bookingCounter', (counter + 1).toString());
  
  return bookingNumber;
};

  const formatBookingMessage = (bookingData) => {
    const {
      fullName,
      email,
      phone,
      pickupAddress,
      dropAddress,
      pickupDate,
      pickupTime,
      tripType,
      vehicleName,
      specialRequirements,
      passengers,
      luggage,
      paymentMethod
    } = bookingData;

    const message = `*NEW BOOKING REQUEST FROM MYJOURNEYCAB*
*Booking Id:* ${generateBookingNumber()}
*Customer Details:*
 Name: ${fullName}
 Email: ${email}
 Phone: ${phone}

*Trip Details:*
 Pickup: ${pickupAddress}
 Drop: ${dropAddress}
 Date: ${pickupDate}
 Time: ${pickupTime}
 Trip Type: ${tripType}

*Vehicle Details:*
 Vehicle: ${vehicleName}
 Passengers: ${passengers}
 Luggage: ${luggage} pieces

*Payment & Requirements:*
 Payment Method: ${paymentMethod}
 Special Requirements: ${specialRequirements || 'None'}



Please contact the customer immediately to confirm this booking.`;

    return message;
  };

  const sendToWhatsApp = (bookingData) => {
    const message = formatBookingMessage(bookingData);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${919728115781}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate vehicle selection
    if (!selectedVehicle || !formData.vehicleType || !isVehicleSelected) {
      setVehicleSelectionError('Please select a vehicle to continue with your booking');
      if (vehicleSectionRef.current) {
        vehicleSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }

    // Validate all required fields
    if (!formData.fullName || !formData.email || !formData.phone ||
      !formData.pickupAddress || !formData.dropAddress ||
      !formData.pickupDate || !formData.pickupTime) {
      alert('Please fill all required fields before submitting the booking.');
      return;
    }

    setIsSubmitting(true);
    setVehicleSelectionError('');

    try {
      // Prepare booking data
      const bookingData = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        pickupAddress: formData.pickupAddress.trim(),
        dropAddress: formData.dropAddress.trim(),
        pickupDate: formData.pickupDate,
        pickupTime: formData.pickupTime,
        tripType: formData.tripType,
        vehicleName: formData.vehicleName,
        specialRequirements: formData.specialRequirements.trim(),
        passengers: parseInt(formData.passengers) || 1,
        luggage: parseInt(formData.luggage) || 1,
        paymentMethod: formData.paymentMethod
      };

      // Generate booking ID
      const newBookingId = generateBookingNumber();
      setBookingId(newBookingId);

      // Send to WhatsApp
      sendToWhatsApp(bookingData);

      // Show success message
      setIsSubmitted(true);

      // Redirect after delay
      setTimeout(() => {
        navigate('/vehicles');
      }, 8000); // Increased timeout to give user time to see the WhatsApp redirect

    } catch (error) {
      console.error('Error processing booking:', error);
      alert('Failed to process booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  const calculateEstimatedAmount = () => {
    try {
      if (!selectedVehicle) return '₹0';
      const pricing = getVehiclePricing(selectedVehicle);
      const baseAmount = pricing?.minCharge?.replace('₹', '') || '200';
      const serviceFee = 50;
      return `₹${parseInt(baseAmount) + serviceFee}`;
    } catch (error) {
      return '₹0';
    }
  };

 

  const PreFilledBadge = () => {
    if (!preFilledData) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
      >
        <div className="flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800 font-medium">Route pre-filled from popular destinations!</span>
        </div>
        <p className="text-green-700 text-sm mt-1">
          Your route from {preFilledData.pickupAddress} to {preFilledData.dropAddress} has been automatically set.
        </p>
      </motion.div>
    );
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircle className="h-8 w-8 text-green-600" />
          </motion.div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Booking Confirmed!
          </h2>
          <p className="text-gray-600 mb-4">
            Your {getVehicleName(selectedVehicle)} has been successfully booked.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">Booking Reference</p>
            <p className="font-mono font-bold text-lg text-gray-900">{bookingId}</p>
            <p className="text-sm text-gray-600 mt-2">Vehicle</p>
            <p className="font-semibold text-gray-900">{getVehicleName(selectedVehicle)}</p>
          </div>
          <p className="text-sm text-gray-500">
            You will receive a confirmation email shortly.
            <br />
            Redirecting to vehicles page in 5 seconds...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      ref={formRef}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-cyan-50/30"
    >
      <div className="container mx-auto px-4 max-w-6xl py-8">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/vehicles')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-gray-200 hover:border-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Vehicles</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
            Book Your Premium Ride
          </h1>
          <div className="w-20"></div>
        </motion.div>

        {/* Show pre-filled badge if data comes from PopularPlaces */}
        <PreFilledBadge />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100"
            >
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Enhanced Vehicle Selection - Now Required */}
                <div 
                  id="vehicle-selection-section"
                  ref={vehicleSectionRef}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <Car className="h-6 w-6 mr-3 text-blue-600" />
                    Select Your Vehicle *
                    <span className="text-red-500 ml-1">(Required)</span>
                  </h3>
                  
                  {/* Vehicle Selection Error */}
                  {vehicleSelectionError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4"
                    >
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <span className="text-red-800 font-medium">{vehicleSelectionError}</span>
                      </div>
                    </motion.div>
                  )}
                  
                  <div className="space-y-4">
                    {/* Current Vehicle Display - Enhanced */}
                    <motion.div 
                      onClick={() => setShowVehicleSelector(!showVehicleSelector)}
                      whileHover={{ scale: 1.02 }}
                      className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                        !selectedVehicle 
                          ? 'border-red-300 bg-red-50 border-dashed animate-pulse' 
                          : vehicleSelectionError 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50 border-solid'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center space-x-4">
                          <div className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex items-center justify-center ${
                            !selectedVehicle ? 'border-red-200 bg-red-100' : 'border-white shadow-lg bg-gray-100'
                          }`}>
                            {selectedVehicle ? (
                              <img
                                src={getVehicleImage(selectedVehicle)}
                                alt={getVehicleName(selectedVehicle)}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = defaultVehicleImage;
                                }}
                              />
                            ) : (
                              <Car className="h-6 w-6 text-red-400" />
                            )}
                          </div>
                          <div>
                            {selectedVehicle ? (
                              <>
                                <p className="font-bold text-gray-900 text-lg">{getVehicleName(selectedVehicle)}</p>
                                <p className="text-gray-600 text-sm">{getVehicleDescription(selectedVehicle)}</p>
                              </>
                            ) : (
                              <>
                                <p className="font-bold text-red-700 text-lg">No Vehicle Selected</p>
                                <p className="text-red-600 text-sm">Please select a vehicle to continue</p>
                              </>
                            )}
                            {vehicleSelectionError && (
                              <p className="text-red-600 text-xs mt-1 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Please select a vehicle
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {selectedVehicle ? (
                            <>
                              <p className="font-bold text-blue-600 text-xl">{getVehiclePricing(selectedVehicle).perKm}</p>
                            </>
                          ) : (
                            <p className="font-bold text-red-600 text-lg">Select Vehicle</p>
                          )}
                          <div className="flex items-center justify-end mt-2 text-blue-600">
                            <span className="text-sm font-medium">
                              {selectedVehicle ? 'Change Vehicle' : 'Select Vehicle'}
                            </span>
                            {showVehicleSelector ? (
                              <ChevronUp className="h-4 w-4 ml-1" />
                            ) : (
                              <ChevronDown className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Enhanced Vehicle Selector Dropdown */}
                    {showVehicleSelector && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-lg max-h-96 overflow-y-auto"
                      >
                        {vehicleCategories.map((vehicle) => (
                          <motion.div
                            key={vehicle.id}
                            onClick={() => handleVehicleSelect(vehicle)}
                            whileHover={{ backgroundColor: "rgba(59, 130, 246, 0.05)" }}
                            className={`p-4 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-200 ${
                              selectedVehicle?.id === vehicle.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center">
                                  <img
                                    src={getVehicleImage(vehicle)}
                                    alt={getVehicleName(vehicle)}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.src = defaultVehicleImage;
                                    }}
                                  />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">{getVehicleName(vehicle)}</p>
                                  <p className="text-gray-600 text-sm">{getVehicleDescription(vehicle)}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-blue-600">{getVehiclePricing(vehicle).perKm}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-3">
                              {getVehicleFeatures(vehicle).slice(0, 3).map((feature, index) => (
                                <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  {feature}
                                </span>
                              ))}
                              {getVehicleFeatures(vehicle).length > 3 && (
                                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                                  +{getVehicleFeatures(vehicle).length - 3} more
                                </span>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <User className="h-6 w-6 mr-3 text-blue-600" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your email"
                      />
                    </div>
                   <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Phone Number *
  </label>
  <input
    type="tel"
    name="phone"
    value={formData.phone}
    onChange={handleInputChange}
    required
    maxLength="10"
    pattern="[0-9]{10}"
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
    placeholder="Enter your 10-digit phone number"
  />
</div>
                  </div>
                </div>

                {/* Trip Details with Location Autocomplete */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <MapPin className="h-6 w-6 mr-3 text-green-600" />
                    Trip Details
                  </h3>
                  <div className="space-y-6">

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Trip Type
                      </label>
                      <select
                        name="tripType"
                        value={formData.tripType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="oneWay">One Way</option>
                        <option value="roundTrip">Round Trip</option>
                        <option value="outstation">Outstation</option>
                        <option value="airport">Airport Transfer</option>
                        <option value="rental">Rentals</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Address *
                      </label>
                      <LocationAutocomplete
                        value={formData.pickupAddress}
                        onChange={handleInputChange}
                        placeholder="Start typing to search locations..."
                        type="pickup"
                        onLocationSelect={(location) => handleLocationSelect(location, 'pickup')}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Start typing to see location suggestions (minimum 3 characters)
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Drop Address *
                      </label>
                      <LocationAutocomplete
                        value={formData.dropAddress}
                        onChange={handleInputChange}
                        placeholder="Start typing to search locations..."
                        type="drop"
                        onLocationSelect={(location) => handleLocationSelect(location, 'drop')}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Start typing to see location suggestions (minimum 3 characters)
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Date *
                        </label>
                        <input
                          type="date"
                          name="pickupDate"
                          value={formData.pickupDate}
                          onChange={handleInputChange}
                          required
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Time *
                        </label>
                        <input
                          type="time"
                          name="pickupTime"
                          value={formData.pickupTime}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                  </div>
                </div>

                {/* Additional Requirements */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Additional Requirements
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Passengers
                      </label>
                      <select
                        name="passengers"
                        value={formData.passengers}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                          <option key={num} value={num}>{num} Passenger{num > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Luggage Pieces
                      </label>
                      <select
                        name="luggage"
                        value={formData.luggage}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        {[1, 2, 3, 4, 5, 6].map(num => (
                          <option key={num} value={num}>{num} Bag{num > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Special Requirements
                    </label>
                    <textarea
                      name="specialRequirements"
                      value={formData.specialRequirements}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Any special requirements, notes, or additional instructions..."
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Payment Method
                  </h3>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="cash">Cash Payment</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="upi">UPI Payment</option>
                    <option value="wallet">Digital Wallet</option>
                  </select>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting || !isVehicleSelected}
                  whileHover={isVehicleSelected && !isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={isVehicleSelected && !isSubmitting ? { scale: 0.98 } : {}}
                  className={`w-full text-white py-4 px-8 rounded-xl font-bold text-lg shadow-2xl transition-all duration-300 flex items-center justify-center space-x-3 ${isVehicleSelected && !isSubmitting
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-400 shadow-blue-500/30 hover:shadow-blue-500/40'
                      : 'bg-gray-400 shadow-gray-400/30 cursor-not-allowed'
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Processing Booking...</span>
                    </>
                  ) : !isVehicleSelected ? (
                    <>
                      <AlertCircle className="h-5 w-5" />
                      <span>Please Select a Vehicle</span>
                    </>
                  ) : (
                    <>
                      <Phone className="h-5 w-5" />
                      <span>Book {getVehicleName(selectedVehicle)}</span>
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>

          {/* Enhanced Booking Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-6 sticky top-8 border border-gray-100"
            >
              {/* Show trip info if coming from PopularPlaces */}
              {tripData && (
                <div className="bg-blue-50 rounded-xl p-4 mb-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Selected Route</h4>
                  <div className="flex items-center space-x-2 text-blue-800">
                    <MapPin className="h-4 w-4" />
                    <span className="font-medium">{tripData.from} → {tripData.to}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm text-blue-700">
                    <span>{tripData.distance}</span>
                    <span>{tripData.duration}</span>
                  </div>
                </div>
              )}

              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Booking Summary
              </h3>

              {selectedVehicle ? (
                <div className="space-y-6">
                  {/* Vehicle Info */}
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-3 rounded-xl overflow-hidden border-2 border-blue-200 shadow-lg bg-gray-100 flex items-center justify-center">
                      <img
                        src={getVehicleImage(selectedVehicle)}
                        alt={getVehicleName(selectedVehicle)}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = defaultVehicleImage;
                        }}
                      />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">{getVehicleName(selectedVehicle)}</h4>
                    <p className="text-gray-600 text-sm mt-1">{getVehicleDescription(selectedVehicle)}</p>
                  </div>

                  {/* Vehicle Specifications */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 text-center">Vehicle Specifications</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="text-center">
                        <Users className="h-4 w-4 mx-auto text-blue-600 mb-1" />
                        <p className="font-medium text-gray-900">{getVehicleSpecifications(selectedVehicle).seats} Seats</p>
                      </div>
                      <div className="text-center">
                        <Car className="h-4 w-4 mx-auto text-green-600 mb-1" />
                        <p className="font-medium text-gray-900">{getVehicleSpecifications(selectedVehicle).luggage} Bags</p>
                      </div>
                      <div className="text-center">
                        <Settings className="h-4 w-4 mx-auto text-purple-600 mb-1" />
                        <p className="font-medium text-gray-900">{getVehicleSpecifications(selectedVehicle).transmission}</p>
                      </div>
                      <div className="text-center">
                        <Fuel className="h-4 w-4 mx-auto text-orange-600 mb-1" />
                        <p className="font-medium text-gray-900">{getVehicleSpecifications(selectedVehicle).fuel}</p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Breakdown */}
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <h4 className="font-semibold text-gray-900 mb-3">Pricing Breakdown</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Fare</span>
                        <span className="font-semibold">{getVehiclePricing(selectedVehicle).minCharge}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Distance Charge</span>
                        <span className="font-semibold">{getVehiclePricing(selectedVehicle).perKm}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Hourly Rate</span>
                        <span className="font-semibold">{getVehiclePricing(selectedVehicle).perHour}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2">
                        {/* <div className="flex justify-between text-lg font-bold">
                          <span>Estimated Total</span>
                          <span className="text-blue-600">{calculateEstimatedAmount()}</span>
                        </div> */}
                      </div>
                    </div>
                  </div>

                  {/* Included Features */}
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-3 text-center">What's Included</h4>
                    <ul className="text-sm text-blue-800 space-y-2">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 mr-2 text-green-500" />
                        Professional chauffeur
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 mr-2 text-green-500" />
                        Fuel and toll charges
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 mr-2 text-green-500" />
                        GST included
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 mr-2 text-green-500" />
                        24/7 customer support
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 mr-2 text-green-500" />
                        Free cancellation up to 2 hours
                      </li>
                    </ul>
                  </div>

                  {/* Quick Features */}
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-xl p-4 text-white">
                    <h4 className="font-semibold mb-3 text-center">Quick Features</h4>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {getVehicleFeatures(selectedVehicle).slice(0, 4).map((feature, index) => (
                        <span key={index} className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No Vehicle Selected</p>
                  <p className="text-gray-500 text-sm mt-2">Please select a vehicle to see booking summary</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;